import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, ShieldCheck, Clock, FileX2, AlertTriangle, Car } from "lucide-react";
import { api } from "../../services/api";

function Tile({ icon: Icon, label, value, tone }) {
  return (
    <div className="metric" style={{ borderTopColor: tone }}>
      <div className="metric-icon" style={{ background: `${tone}14`, color: tone }}><Icon size={18} /></div>
      <div className="metric-value">{value}</div>
      <div className="metric-label">{label}</div>
    </div>
  );
}

const FILTERS = [
  { id: "", label: "All" },
  { id: "COMPLIANCE FLAG", label: "Exceptions" },
  { id: "VERIFICATION REQUIRED", label: "Verify" },
  { id: "UPCOMING RISK", label: "Upcoming" },
  { id: "PRE-VALIDATED", label: "Pre-validated" },
];

export default function RVFleet() {
  const [data, setData] = useState(null);
  const [uploaded, setUploaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const nav = useNavigate();

  useEffect(() => { api.rvFleet().then(setData); }, []);

  const upload = () => { setBusy(true); setTimeout(() => { setBusy(false); setUploaded(true); }, 1200); };

  const rows = useMemo(() => {
    if (!data) return [];
    return filter ? data.vehicles.filter((v) => v.state === filter) : data.vehicles;
  }, [data, filter]);

  if (!data) return <div className="loading-page">Loading fleet compliance roster…</div>;
  const s = data.summary;

  return (
    <div className="dash" data-testid="rv-fleet-page">
      <div className="vans-eyebrow" style={{ color: "#b91c1c" }}>IGL · Refuel Validation · Fleet</div>
      <h1 className="vans-title" style={{ margin: "4px 0 6px" }}>Fleet Roster — Bulk Pre-validation</h1>
      <p className="vans-sub" style={{ maxWidth: 760, marginBottom: 16 }}>
        Submit a fleet roster in bulk and DotIQ continuously tells you which plates have <b>upcoming expirations</b>,
        <b> missing records</b> or <b>configuration exceptions</b> — before any of them reach a station.
      </p>

      <div className="rv-upload-bar">
        <div className="rv-upload-left"><UploadCloud size={18} /> {uploaded ? `Roster ingested — ${s.total} vehicles reconciled against VAHAN / PESO records` : "Upload a fleet roster (CSV of registrations) for bulk reconciliation"}</div>
        <button className="btn-primary" onClick={upload} disabled={busy || uploaded} data-testid="rv-fleet-upload">
          <UploadCloud size={15} className={busy ? "spin" : ""} /> {busy ? "Reconciling…" : uploaded ? "Ingested" : "Upload roster (sample)"}
        </button>
      </div>

      <div className="metrics-row" style={{ marginTop: 16 }}>
        <Tile icon={Car} label="Vehicles in roster" value={s.total} tone="#0f172a" />
        <Tile icon={ShieldCheck} label="Pre-validated" value={s.pre_validated} tone="#059669" />
        <Tile icon={Clock} label="Upcoming expirations" value={s.upcoming} tone="#ca8a04" />
        <Tile icon={FileX2} label="Missing records" value={s.missing} tone="#ea580c" />
        <Tile icon={AlertTriangle} label="Configuration exceptions" value={s.exceptions} tone="#dc2626" />
      </div>

      <div className="rv-fleet-filters">
        {FILTERS.map((f) => (
          <button key={f.id} className={`rv-chip ${filter === f.id ? "active" : ""}`} onClick={() => setFilter(f.id)} data-testid={`rv-fleet-filter-${f.id || "all"}`}>{f.label}</button>
        ))}
        <span className="filter-count">{rows.length} vehicle{rows.length !== 1 ? "s" : ""}</span>
      </div>

      <section className="panel">
        <div className="rv-table">
          <div className="rv-thead rv-fleet-head">
            <span>Plate</span><span>Fleet</span><span>Risk</span><span>Reason</span><span>Next hydro-test</span>
          </div>
          {rows.map((v) => (
            <div key={v.plate} className="rv-row rv-fleet-row" data-testid={`rv-fleet-${v.plate}`}
                 onClick={() => nav(`/iglrv/vehicles/${v.plate}`)} role="button">
              <span className="rv-plate">{v.plate}</span>
              <span className="rv-area">{v.fleet}</span>
              <span><i className={`rv-dot ${v.state_meta.dot}`} /> {v.state_meta.chip}</span>
              <span className="rv-reason">{v.reason}</span>
              <span className="rv-area">
                {v.next_due ? v.next_due : "—"}
                {v.days_to_expiry != null && v.days_to_expiry >= 0 && v.days_to_expiry <= 30 && <b className="rv-exp"> · {v.days_to_expiry}d</b>}
                {v.days_to_expiry != null && v.days_to_expiry < 0 && <b className="rv-exp over"> · expired</b>}
              </span>
            </div>
          ))}
        </div>
      </section>

      <footer className="vans-foot">DotIQ × IGL — Refuel Validation POC · click any vehicle to open its intelligence view · synthetic roster for demo.</footer>
    </div>
  );
}
